// ============================================================================
// Intel HEX Format Parser (.hex files from GCC/KEIL)
// ============================================================================

export interface HexRecord {
  type: number;          // 00=data, 01=EOF, 02=ext seg, 04=ext linear, 05=start linear
  address: number;
  data: Uint8Array;
}

export interface ParsedHex {
  data: Uint8Array;
  baseAddress: number;
  entryPoint?: number;
}

/**
 * Parse an Intel HEX file into a binary image.
 */
export function parseIntelHex(hexContent: string): ParsedHex {
  const lines = hexContent.split(/\r?\n/).filter((l) => l.startsWith(':'));
  const records: HexRecord[] = [];
  let extendedAddress = 0;
  let entryPoint: number | undefined;

  for (const line of lines) {
    const record = parseRecord(line);
    if (!record) continue;

    switch (record.type) {
      case 0x00: // Data record
        record.address += extendedAddress;
        records.push(record);
        break;
      case 0x01: // EOF
        break;
      case 0x02: // Extended segment address
        extendedAddress = ((record.data[0] << 8) | record.data[1]) << 4;
        break;
      case 0x04: // Extended linear address
        extendedAddress = ((record.data[0] << 8) | record.data[1]) << 16;
        break;
      case 0x05: // Start linear address
        entryPoint =
          (record.data[0] << 24) |
          (record.data[1] << 16) |
          (record.data[2] << 8) |
          record.data[3];
        break;
    }
  }

  if (records.length === 0) {
    return { data: new Uint8Array(0), baseAddress: 0 };
  }

  // Compute address range
  let minAddr = Infinity;
  let maxAddr = 0;
  for (const r of records) {
    minAddr = Math.min(minAddr, r.address);
    maxAddr = Math.max(maxAddr, r.address + r.data.length);
  }

  // Create contiguous binary image (fill gaps with 0xFF)
  const size = maxAddr - minAddr;
  const binary = new Uint8Array(size);
  binary.fill(0xFF);

  for (const r of records) {
    binary.set(r.data, r.address - minAddr);
  }

  return { data: binary, baseAddress: minAddr, entryPoint };
}

function parseRecord(line: string): HexRecord | null {
  if (!line.startsWith(':') || line.length < 11) return null;

  const bytes: number[] = [];
  for (let i = 1; i < line.length; i += 2) {
    bytes.push(parseInt(line.substring(i, i + 2), 16));
  }

  // Verify checksum
  const sum = bytes.reduce((a, b) => a + b, 0) & 0xFF;
  if (sum !== 0) return null;

  const byteCount = bytes[0];
  const address = (bytes[1] << 8) | bytes[2];
  const type = bytes[3];
  const data = new Uint8Array(bytes.slice(4, 4 + byteCount));

  return { type, address, data };
}
